import {
    Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, WithStyles
} from "@material-ui/core";
import { StyleRules, Theme, withStyles } from "@material-ui/core/styles";
import autobind from "autobind-decorator";
import { computed, observable } from "mobx";
import { observer } from "mobx-react";
import * as QS from "querystring";
import * as React from "react";
import StripeCheckout, { Token } from "react-stripe-checkout";

import Domain from "@material-ui/icons/Domain";
import People from "@material-ui/icons/People";
import Person from "@material-ui/icons/Person";

import { API, Button, GithubInvite, Text } from "..";
import promo from "../Promo";
import LicenseComp, { formatPrice, License }  from "./License";

declare function buy_sketch_elements_conversion(): void;

type ClassNames = "licenses" | "card" | "link" | "actions";

const licenses: License[] = [
    {
        id: "single",
        title: "Single",
        label: "One iOS app and one Android app.",
        discount: 1950,
        price: 7500,
        icon: Person
    },
    {
        id: "multiple",
        title: "Multiple",
        label: "Five iOS app and five Android apps.",
        discount: 3900,
        price: 15000,
        icon: People
    },
    {
        id: "unlimited",
        title: "Unlimited",
        label: "Unlimited iOS and Android apps.",
        discount: 7800,
        price: 29900,
        icon: Domain
    }
];
const styles = (theme: Theme): StyleRules<ClassNames> => ({
    licenses: {
        display: "flex",
        justifyContent: "space-between",
        [theme.breakpoints.down("xs")]: {
            flexDirection: "column"
        }
    },
    card: {
        margin: theme.spacing.unit
    },
    link: {
        color: theme.typography.body1.color
    },
    actions: {
        marginRight: theme.spacing.unit * 3
    }
});

interface CheckoutDialogProps {
    open: boolean,
    onClose: () => void,
}

@observer
class CheckoutDialog extends React.Component<WithStyles<ClassNames> & CheckoutDialogProps> {

    @observable public _license: number = 0;
    @observable public _loading: boolean = false;
    @observable public _error: boolean = false;
    @observable public _optedIn: boolean = false;
    @observable public _url: string | undefined;
    @observable public _key: string;
    @observable public _email: string;

    @computed get license() { return this._license; }
    set license(license: number) { this._license = license; }

    @computed get loading() { return this._loading; }
    set loading(loading: boolean) { this._loading = loading; }

    @computed get error() { return this._error; }
    set error(error: boolean) { this._error = error; }

    @computed get optedIn() { return this._optedIn; }
    set optedIn(optedIn: boolean) { this._optedIn = optedIn; }

    @computed get url() { return this._url; }
    set url(url: string | undefined) { this._url = url; }

    @computed get accessKey() { return this._key; }
    set accessKey(key: string) { this._key = key; }

    @computed get email() { return this._email; }
    set email(email: string) { this._email = email; }

    private key: string = API.isProduction
        ?
            "pk_live_2ruvnlX2Eqa1yYxXjufnGiKA"
        :
            "pk_test_IgNcZsyRG4FkepgyUGEBXAn4";

    @autobind
    public onClose() {
        const {onClose} = this.props;
        this.loading = false;
        this.error = false;
        this.url = undefined;
        onClose();
    }

    @autobind
    public toggleOptedIn() {
        this.optedIn = !this.optedIn;
    }

    public render() {
        const {loading, error, url, onClose} = this;
        const {classes, open} = this.props;
        const license = licenses.filter((l, index) => this.license === index)[0];
        const link = (
            <a
                href="https://wcandillon.github.io/react-native-elements/License.html"
                target="_blank"
                className={classes.link}
            >
                See terms and conditions
            </a>
        );
        const paypal = (
            <a className={classes.link} href="https://gumroad.com/l/react-native-elements" target="_blank">
                here
            </a>
        );
        if (loading) {
            return (
                <Dialog open={true}>
                    <DialogContent>
                        <CircularProgress />
                    </DialogContent>
                </Dialog>
            );
        } else if (error) {
            return (
                <Dialog {...{open, onClose}}>
                    <DialogTitle>An error happened</DialogTitle>
                    <DialogContent>
                        <Text>
The payment processing failed, your credit card hasn't been charged.
Please <a href="mailto:wcandillon@gmail.com" className={classes.link}>contact us</a> if you have any questions.
                        </Text>
                    </DialogContent>
                </Dialog>
            );
        } else if (url) {
            return (
                <Dialog {...{open, onClose}}>
                    <DialogTitle>Thank you for your purchase ❤️</DialogTitle>
                    <DialogContent>
                        <Text>
You can download the package at <a className={classes.link} href={url}>{url}</a>.
                        </Text>
                        <Text>
If you have any questions, please <a className={classes.link} href="mailto:wcandillon@gmail.com">contact us</a>.
                        </Text>
                        <GithubInvite
                            repo="react-native-elements"
                            email={this.email}
                            accessKey={this.accessKey}
                        />
                    </DialogContent>
                    <DialogActions className={classes.actions}>
                        <Button onClick={onClose}>
                          OK
                        </Button>
                    </DialogActions>
                </Dialog>
            );
        }
        return (
            <Dialog {...{open, onClose}}>
                <DialogTitle>React Native Sketch Elements</DialogTitle>
                <DialogContent>
                    <Text variant="footnote">
Please select a license below. {link}.
<br />
Discounts are available until {promo.ends}.
You can checkout using PayPal {paypal}.
                    </Text>
                    <div className={classes.licenses}>
                    {
                        licenses.map((l, index) =>
                            <LicenseComp
                                key={l.id}
                                selected={this.license === index}
                                // tslint:disable-next-line:jsx-no-lambda
                                onClick={() => this.license = index}
                                license={l}
                            />
                        )
                    }
                    </div>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={this.optedIn}
                          onChange={this.toggleOptedIn}
                          value="true"
                          color="primary"
                        />
                      }
                      label={(
<Text variant="footnote" gutterBottom={false}>
    Send me updates and offers on React Native Starter Kits.
</Text>
                      )}
                    />
                </DialogContent>
                <DialogActions className={classes.actions}>
                    <StripeCheckout
                         ComponentClass="span"
                         token={this.onToken}
                         stripeKey={this.key}
                         amount={promo.active ? license.discount : license.price}
                         currency="USD"
                    >
                        <Button primary={true}>
                            ${formatPrice(promo.active ? license.discount : license.price)} Buy Now
                        </Button>
                    </StripeCheckout>
                </DialogActions>
            </Dialog>
        );
    }

    @autobind
    private async onToken(token: Token) {
        const {endpoint} = API;
        const fn = "getElementsNew";
        const testMode = !API.isProduction;
        this.loading = true;
        try {
            const license = licenses.filter((l, index) => this.license === index)[0];
            const query = {
                testMode,
                license: license.id,
                optedIn: this.optedIn,
                promo: promo.active
            };
            const url = `${endpoint}${fn}?${QS.stringify(query)}`;
            const response = await fetch(url, {
                method: "POST",
                body: JSON.stringify(token),
                headers: {
                    "Accept": "application/json, text/plain, */*",
                    "Content-Type": "application/json"
                }
            });
            if (response.status === 200) {
                const data = await response.json();
                this.url = data.url;
                this.accessKey = data.key;
                this.email = token.email;
                this.loading = false;
                buy_sketch_elements_conversion();
            } else {
                this.error = true;
                this.loading = false;
            }
        } catch (e) {
            this.error = true;
            this.loading = false;
        }
    }
}

export default withStyles(styles)<CheckoutDialogProps>(CheckoutDialog);



// WEBPACK FOOTER //
// ./src/components/checkout/CheckoutDialog.tsx